import { useState, useEffect } from "react";
import { toast } from "sonner";
import { UploadSimple, Trash, Eye, EyeSlash } from "@phosphor-icons/react";
import { fullUrl, formatFrDate } from "@/components/RealisationCard";

const emptyForm = () => ({ title: "", description: "", service_type: "auto", vehicle_type: "", city: "", department: "Yvelines (78)",
  date: new Date().toISOString().slice(0, 10), image_url: "", before_url: "", after_url: "" });
const inputCls = "w-full p-2 text-sm border border-[#1A1A1A]/20 bg-white";

const readAsDataUrl = (file) => new Promise((res, rej) => {
  const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
});

const PhotoField = ({ label, value, onUpload, testId }) => (
  <label className="block cursor-pointer" data-testid={testId}>
    <span className="text-[#1A1A1A]/60 text-xs block mb-1">{label}</span>
    <div className="h-24 border border-dashed border-[#1A1A1A]/30 bg-white flex items-center justify-center overflow-hidden hover:border-[#1A1A1A]/60 transition-colors">
      {value ? <img src={fullUrl(value)} alt={label} className="w-full h-full object-cover" />
        : <span className="text-xs text-[#1A1A1A]/40 flex items-center gap-1"><UploadSimple size={14} /> Choisir</span>}
    </div>
    <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => e.target.files[0] && onUpload(e.target.files[0])} />
  </label>
);

const AdminRealisationsPanel = ({ propAx }) => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [mode, setMode] = useState("slider");
  const [busy, setBusy] = useState(false);

  const load = () => propAx.get("/admin/realisations").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const upload = (field) => async (file) => {
    setBusy(true);
    try {
      const { data } = await propAx.post("/upload-photo", { file_data: await readAsDataUrl(file), file_name: file.name });
      setForm((p) => ({ ...p, [field]: data.url }));
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur d'upload"); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    if (!form.title || !form.city || !form.date) return toast.error("Titre, ville et date sont obligatoires");
    const payload = mode === "slider" ? { ...form, image_url: "" } : { ...form, before_url: "", after_url: "" };
    setBusy(true);
    try {
      await propAx.post("/admin/realisations", payload);
      toast.success("Réalisation publiée");
      setForm(emptyForm()); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const toggle = (id) => propAx.put(`/admin/realisations/${id}/publish`).then(load).catch(() => toast.error("Erreur"));
  const remove = (id) => window.confirm("Supprimer cette réalisation ?") &&
    propAx.delete(`/admin/realisations/${id}`).then(() => { toast.success("Supprimée"); load(); }).catch(() => toast.error("Erreur"));

  return (
    <div className="bg-white border border-[#1A1A1A]/10 p-5 mb-6 space-y-5" data-testid="realisations-panel">
      <h3 className="text-sm font-medium text-[#1A1A1A]">Nouvelle réalisation avant / après</h3>
      <div className="grid sm:grid-cols-2 gap-3">
        <input placeholder="Titre (ex. Remise au propre d'un SUV)" value={form.title} onChange={set("title")} className={inputCls} data-testid="real-title" />
        <select value={form.service_type} onChange={set("service_type")} className={inputCls} data-testid="real-service">
          <option value="auto">Automobile</option><option value="mobilier">Mobilier</option>
          <option value="appartement">Appartement</option><option value="commercial">Locaux</option>
        </select>
        <input placeholder="Ville (ex. Versailles)" value={form.city} onChange={set("city")} className={inputCls} data-testid="real-city" />
        <input placeholder="Département" value={form.department} onChange={set("department")} className={inputCls} data-testid="real-department" />
        <input type="date" value={form.date} onChange={set("date")} className={inputCls} data-testid="real-date" />
        <input placeholder="Véhicule / objet (optionnel)" value={form.vehicle_type} onChange={set("vehicle_type")} className={inputCls} data-testid="real-vehicle" />
      </div>
      <textarea placeholder="Prestations réalisées (shampoing sièges, plastiques…)" rows={2} value={form.description} onChange={set("description")}
        className={`${inputCls} resize-none`} data-testid="real-description" />
      <div className="flex gap-2">
        {[["slider", "2 photos (curseur)"], ["single", "1 photo montage"]].map(([m, l]) => (
          <button key={m} type="button" onClick={() => setMode(m)} data-testid={`real-mode-${m}`}
            className={`px-3 py-1 text-xs border ${mode === m ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "border-[#1A1A1A]/20 text-[#1A1A1A]/60"}`}>{l}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3 max-w-md">
        {mode === "slider" ? (<>
          <PhotoField label="Photo AVANT" value={form.before_url} onUpload={upload("before_url")} testId="real-before" />
          <PhotoField label="Photo APRÈS" value={form.after_url} onUpload={upload("after_url")} testId="real-after" />
        </>) : <PhotoField label="Photo montage" value={form.image_url} onUpload={upload("image_url")} testId="real-image" />}
      </div>
      <button onClick={submit} disabled={busy} data-testid="real-submit"
        className="bg-[#1A1A1A] text-white px-4 py-2 text-xs uppercase tracking-wide hover:bg-[#333] disabled:opacity-50">
        {busy ? "Patientez…" : "Publier la réalisation"}
      </button>

      <div className="border-t border-[#1A1A1A]/10 pt-4 space-y-2">
        <p className="text-xs text-[#1A1A1A]/50 uppercase tracking-wide">Réalisations ({items.length})</p>
        {items.map((it) => (
          <div key={it.id} className="flex items-center gap-3 p-2 bg-[#EDEBE4]/50" data-testid={`admin-real-${it.id}`}>
            <img src={fullUrl(it.after_url || it.image_url)} alt="" className="w-14 h-14 object-cover shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-[#1A1A1A] truncate">{it.title}</p>
              <p className="text-[11px] text-[#1A1A1A]/50">{it.city} — {formatFrDate(it.date)} — {it.published ? "Publiée" : "Masquée"}</p>
            </div>
            <button onClick={() => toggle(it.id)} className="p-1.5 border border-[#1A1A1A]/20 text-[#1A1A1A]/60" title={it.published ? "Masquer" : "Publier"} data-testid={`real-toggle-${it.id}`}>
              {it.published ? <EyeSlash size={13} /> : <Eye size={13} />}
            </button>
            <button onClick={() => remove(it.id)} className="p-1.5 border border-red-300 text-red-600" title="Supprimer" data-testid={`real-delete-${it.id}`}><Trash size={13} /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminRealisationsPanel;
